import NativeTabBar from "../../shared/NativeTabBar";
import ToolModeTab from "./tabs/ToolModeTab";
import ArgumentsTab from "./tabs/ArgumentsTab";
import SelectorsTab from "./tabs/SelectorsTab";
import PreviewTab from "./tabs/PreviewTab";
import VerificationTab from "./tabs/VerificationTab";
import StepMetaInfoRunInfo from "./metaInfo/StepMetaInfoRunInfo";
import styles from "../../../../../styles/automationStudio/automationStudio.module.scss";
import { LABELS } from "../../../content";
import { extractAutomationScreenshotPath } from "../../../../../utils/screenshotUtils";

export default function StepEditorTabPanels({
    isVerificationStep,
    safeActiveTab,
    setActiveTab,
    hasStepMetaInfo,
    showSelectorsTab,
    showPreviewTab,
    localStep,
    displayStep,
    handleLocalStepChange,
    isEditorLocked,
    readOnlyEditor,
    mcpToolsList,
    mcpToolMap,
    executedCodeStrategyType,
    fileSuggestions,
    fileDescriptions,
    variablesList,
    variablesListDescriptions,
    instructions,
    replayExecutionResults,
    replayProgressMap,
    isReplayFlow,
    showDynamicStepBadge,
    replayEval,
    detailForUi,
    statusOptions,
    metaInfoModel,
    selectorsTabProps = {},
    fieldDirtyMap = null,
}) {
    let nextTab = 2;
    const selectorsTabIdx = showSelectorsTab ? nextTab++ : null;
    const previewTabIdx = showPreviewTab ? nextTab++ : null;
    const metaTabIdx = hasStepMetaInfo ? nextTab++ : null;

    let verificationNextTab = 1;
    const verificationMetaTabIdx = hasStepMetaInfo ? verificationNextTab++ : null;

    const verificationTabItems = [
        { value: 0, label: LABELS.TAB_VERIFICATION },
        ...(hasStepMetaInfo ? [{ value: verificationMetaTabIdx, label: LABELS.TAB_META_INFO }] : []),
    ];
    const mainTabItems = [
        { value: 0, label: LABELS.TAB_TOOL_MODE },
        { value: 1, label: LABELS.TAB_ARGUMENTS },
        ...(showSelectorsTab ? [{ value: selectorsTabIdx, label: LABELS.TAB_SELECTORS }] : []),
        ...(showPreviewTab ? [{ value: previewTabIdx, label: LABELS.TAB_PREVIEW }] : []),
        ...(hasStepMetaInfo ? [{ value: metaTabIdx, label: LABELS.TAB_META_INFO }] : []),
    ];

    return (
        <>
            <div className={styles.tabBar}>
                {isVerificationStep ? (
                    <NativeTabBar value={safeActiveTab} onChange={setActiveTab} items={verificationTabItems} />
                ) : (
                    <NativeTabBar value={safeActiveTab} onChange={setActiveTab} items={mainTabItems} />
                )}
            </div>

            <div className={styles.tabContent}>
                {isVerificationStep ? (
                    <>
                        {safeActiveTab === 0 && (
                            <VerificationTab
                                step={displayStep}
                                onStepChange={handleLocalStepChange}
                                isExecuting={isEditorLocked}
                                isReplayFlow={isReplayFlow}
                                isDynamicStep={showDynamicStepBadge}
                                fileSuggestions={fileSuggestions}
                                fileDescriptions={fileDescriptions}
                                variablesList={variablesList}
                                variablesListDescriptions={variablesListDescriptions}
                                replayEval={replayEval}
                                execDetail={detailForUi}
                                statusOptions={statusOptions}
                                instructions={instructions}
                                screenshotRaw={extractAutomationScreenshotPath(detailForUi, displayStep)}
                                fieldDirtyMap={fieldDirtyMap}
                            />
                        )}
                        {hasStepMetaInfo && safeActiveTab === verificationMetaTabIdx && (
                            <StepMetaInfoRunInfo
                                intentType={metaInfoModel.intentType}
                                intentText={metaInfoModel.intentText}
                                bindingRows={metaInfoModel.bindingRows}
                                intentTokens={metaInfoModel.intentTokens}
                            />
                        )}
                    </>
                ) : (
                    <>
                        {safeActiveTab === 0 && (
                            <ToolModeTab
                                step={localStep}
                                onStepChange={handleLocalStepChange}
                                isExecuting={isEditorLocked}
                                readOnlyForMetaInfo={readOnlyEditor}
                                mcpToolsList={mcpToolsList}
                                mcpToolMap={mcpToolMap}
                                executionStatus={displayStep?.status}
                                executionErrorMessage={displayStep?.error}
                                executedCodeStrategyType={executedCodeStrategyType}
                                fileSuggestions={fileSuggestions}
                                fileDescriptions={fileDescriptions}
                                variablesList={variablesList}
                                variablesListDescriptions={variablesListDescriptions}
                                selectorsTabProps={selectorsTabProps}
                                fieldDirtyMap={fieldDirtyMap}
                            />
                        )}
                        {safeActiveTab === 1 && (
                            <ArgumentsTab
                                step={localStep}
                                onStepChange={handleLocalStepChange}
                                isExecuting={isEditorLocked}
                                readOnlyForMetaInfo={readOnlyEditor}
                                mcpToolMap={mcpToolMap}
                                fileSuggestions={fileSuggestions}
                                fileDescriptions={fileDescriptions}
                                variablesList={variablesList}
                                variablesListDescriptions={variablesListDescriptions}
                                argsFieldDirty={fieldDirtyMap?.argsByKey ?? null}
                            />
                        )}
                        {showSelectorsTab && safeActiveTab === selectorsTabIdx && (
                            <SelectorsTab
                                isExecuting={isEditorLocked}
                                readOnlyForMetaInfo={readOnlyEditor}
                                fileSuggestions={fileSuggestions}
                                fileDescriptions={fileDescriptions}
                                variablesList={variablesList}
                                variablesListDescriptions={variablesListDescriptions}
                                strategyDirtyById={fieldDirtyMap?.strategyDirtyById ?? null}
                                {...selectorsTabProps}
                            />
                        )}
                        {showPreviewTab && safeActiveTab === previewTabIdx && (
                            <PreviewTab
                                step={displayStep}
                                instructions={instructions}
                                replayExecutionResults={replayExecutionResults}
                                replayProgressMap={replayProgressMap}
                                isReplayFlow={isReplayFlow}
                            />
                        )}
                        {hasStepMetaInfo && safeActiveTab === metaTabIdx && (
                            <StepMetaInfoRunInfo
                                intentType={metaInfoModel.intentType}
                                intentText={metaInfoModel.intentText}
                                bindingRows={metaInfoModel.bindingRows}
                                intentTokens={metaInfoModel.intentTokens}
                            />
                        )}
                    </>
                )}
            </div>
        </>
    );
}
